import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { DialogSection } from '@/lib/stores/dialogStore';

export type ViewMode = 'edit' | 'write' | 'play' | 'flow';

export interface DialogMetadata {
  dialogId?: string;
  dialogName: string;
  dialogKey: string;
  description: string;
  tags: string[];
  status: string;
  owner: string | null;
  editPermissions: 'Open' | 'Locked';
  startingSection?: string;
  sections: DialogSection[];
  translatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: any[];
}

interface DialogSectionContextType {
  currentSectionIndex: number;
  setCurrentSectionIndex: (index: number) => void;
  currentSection: DialogSection | null;
  setCurrentSection: (section: DialogSection | null) => void;
  onSectionUpdate?: (sectionIndex: number, updates: any) => void;
  setOnSectionUpdate: (callback: (sectionIndex: number, updates: any) => void) => void;
  // Save handler for external components (like sidebar) to trigger dialog save
  onDialogSave?: () => Promise<void>;
  setOnDialogSave: (callback: () => Promise<void>) => void;
  // Metadata update handler for description, tags, etc.
  onMetadataUpdate?: (updates: { 
    description?: string; 
    tags?: string[]; 
    status?: string; 
    owner?: string | null; 
    edit_permissions?: 'Open' | 'Locked'; 
    startingSection?: string;
    translatable?: boolean;
    speakerMode?: 'none' | 'type' | 'free-text' | 'object';
    speakerTypeId?: string | null;
    speakerTemplateIds?: string[];
    dialogVariables?: any[];
  }) => void;
  setOnMetadataUpdate: (callback: (updates: any) => void) => void;
  // Dialog metadata for sidebar (especially for new unsaved dialogs)
  dialogMetadata: DialogMetadata | null;
  setDialogMetadata: (metadata: DialogMetadata | null) => void;
  // Current view mode (for enabling/disabling node-info tab)
  viewMode: ViewMode | null;
  setViewMode: (mode: ViewMode | null) => void;
}

const DialogSectionContext = createContext<DialogSectionContextType | undefined>(undefined);

export const DialogSectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  const [currentSection, setCurrentSection] = useState<DialogSection | null>(null);
  const [onSectionUpdate, setOnSectionUpdateInternal] = useState<((sectionIndex: number, updates: any) => void) | undefined>(undefined);
  const [onDialogSave, setOnDialogSaveInternal] = useState<(() => Promise<void>) | undefined>(undefined);
  const [onMetadataUpdate, setOnMetadataUpdateInternal] = useState<((updates: any) => void) | undefined>(undefined);
  const [dialogMetadata, setDialogMetadata] = useState<DialogMetadata | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode | null>(null);

  const setOnSectionUpdate = useCallback((callback: (sectionIndex: number, updates: any) => void) => {
    setOnSectionUpdateInternal(() => callback);
  }, []);

  const setOnDialogSave = useCallback((callback: () => Promise<void>) => {
    setOnDialogSaveInternal(() => callback);
  }, []);

  const setOnMetadataUpdate = useCallback((callback: (updates: any) => void) => {
    setOnMetadataUpdateInternal(() => callback);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentSectionIndex, 
    setCurrentSectionIndex,
    currentSection,
    setCurrentSection,
    onSectionUpdate,
    setOnSectionUpdate,
    onDialogSave,
    setOnDialogSave,
    onMetadataUpdate,
    setOnMetadataUpdate,
    dialogMetadata,
    setDialogMetadata,
    viewMode,
    setViewMode
  }), [currentSectionIndex, currentSection, onSectionUpdate, setOnSectionUpdate, onDialogSave, setOnDialogSave, onMetadataUpdate, setOnMetadataUpdate, dialogMetadata, viewMode]);

  return (
    <DialogSectionContext.Provider value={contextValue}>
      {children}
    </DialogSectionContext.Provider>
  );
};

export const useDialogSection = () => {
  const context = useContext(DialogSectionContext);
  if (context === undefined) {
    throw new Error('useDialogSection must be used within a DialogSectionProvider');
  }
  return context;
};

// Optional hook that returns null if not in dialog context (for use in StructureSidebar)
export const useDialogSectionOptional = () => {
  return useContext(DialogSectionContext);
};
